import {
  Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  Input,
  ViewChild,
} from '@angular/core';
import { ProjectsList } from '../../models/projects.model';
import { ProjectService } from '../../../core/project.service';
import { NzTabChangeEvent, NzTabSetComponent } from 'ng-zorro-antd/tabs';
import { Router } from '@angular/router';
import {takeUntil, Subject} from "rxjs";

interface GroupedProjects {
  [key: string]: ProjectsList[];
}

@Component({
  selector: 'app-projects-search',
  templateUrl: './projects-search.component.html',
  styleUrls: ['./projects-search.component.scss'],
})
export class ProjectsSearchComponent implements OnInit, OnDestroy {
  @Input() iconSide: 'left' | 'right' | undefined = undefined;
  @Input() label: string = '';
  @Input() defaultPlaceHolder: string = '';

  private _projectId: number | 'all' = 'all';
  @Input() set projectId(value: number | 'all') {
    if (value === 'all') {
      this.searchValue = 'Todos los proyectos';
    }
    this._projectId = value;
  }
  get projectId(): number | 'all' {
    return this._projectId;
  }
  @Output() projectSelected = new EventEmitter<ProjectsList | 'all'>();
  @ViewChild('tabset_projects') tabsetProjects: NzTabSetComponent | undefined;

  public projects: ProjectsList[] = [] as ProjectsList[];
  public projectsGroupedByCompany: GroupedProjects = {} as GroupedProjects;
  public loading: boolean = true;
  public searchValue: string = '';
  public tabActive: number = 0;
  public enabled: boolean = false;
  public isShowingMenu: boolean = false;
  public hasGrouped = false;

  private destroy$: Subject<void> = new Subject<void>();
  constructor(private projectService: ProjectService, private router: Router) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadProjects(): void {
    this.loading = true;
    this.doSearch("");
  }

  doSearch(input: string) {
    const filterMode = this.tabActive == 0 ? 'projects' : 'companies';
    this.projectService.getLimitProjects(input, filterMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
      {
        next: (data) => this.handleProjectsLoadSuccess(data),
        error: (error) => this.handleProjectsLoadError(error)
      }
    );
  }

  private handleProjectsLoadSuccess(data: ProjectsList[]): void {
    this.projects = data;
    this.projects.sort((a, b) => b.projectId - a.projectId);
    this.groupByCompany(this.projects);
    this.loading = false;
    this.enabled = true;
    if (this.projectId !== 'all') {
      this.searchValue = this.projects.find(x => x.projectId === +this.projectId)?.projectName || '';
      this.projectSelected.emit(this.projects.find(x => x.projectId === +this.projectId));
    }
  }

  private handleProjectsLoadError(error: any): void {
    console.error(error);
    this.loading = false;
  }

  /**
   * Sets the active tab based on the provided tab change event.
   *
   * @param tab - An array of NzTabChangeEvent objects representing the tab change events.
   *              The first element's index is used to set the active tab.
   */
  public setTabActive(tab: NzTabChangeEvent[]): void {
    this.tabActive = tab[0].index?.valueOf() || 0;
    this.tabsetProjects?.setSelectedIndex(this.tabActive);
    this.fnSearchValue();
  }

  /**
   * Handles the search input event and updates the search value.
   * If an event is provided, it extracts the value from the event target,
   * converts it to lowercase, and assigns it to the searchValue property.
   * It then resets the projects array to the full projectsList and calls
   * the searchProjects method to filter the projects based on the search value.
   *
   * @param event - The input event containing the search value.
   */
  public fnSearchValue(event?: any): void {
    if (event) {
      this.searchValue = event.target.value.toLowerCase();
      this.doSearch(this.searchValue);
    }
  }

  /**
   * Function trigger when a click is made outside of an market target by the directive `appClickOutsiide`
   */
  public onclickOutside() {
    this.isShowingMenu = false;
  }

  /**
   * Function trigger when the search input is focused
   */
  public onInputfocus() {
    this.isShowingMenu = true;
  }

  /**
   * Handler for click on search results container
   * @param $event The click event object.
   */
  public onClickContainer($event: Event) {
    $event.stopPropagation();
  }


  /**
   * Selects a project and emits the selected project ID.
   * Also updates the search value with the project name.
   *
   * @param project - The name of the selected project.
   */
  public selectProject(project: ProjectsList | 'all'): void {
    this.projectSelected.emit(project);
    this.searchValue = project !== 'all' ? project.projectName : 'Todos los proyectos';
    this.isShowingMenu = false;
  }



  /**
   * Groups a list of projects by their associated company.
   *
   * @param projects - The list of projects to be grouped.
   * @returns void
   *
   * This method takes an array of `ProjectsList` objects and groups them by the `projectCompany` property.
   * The grouped projects are then stored in the `projectsGroupedByCompany` property.
   */
  private groupByCompany(projects: ProjectsList[]): void {
    const groupedByCompany = projects.reduce((acc, project) => {
      if (!acc[project.projectCompany]) {
        acc[project.projectCompany] = [];
      }
      acc[project.projectCompany].push(project);
      return acc;
    }, {} as { [key: string]: ProjectsList[] });


    this.projectsGroupedByCompany = groupedByCompany;
  }

  hasProjectGroupBy() {
    return Object.keys(this.projectsGroupedByCompany).length > 0;
  }

}
